import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Sparkles, Sun, X } from 'lucide-react';
import { toast } from 'react-toastify';
import type { AvailableAssetRef } from '../assetTypeConfig';
import type { VisualEquipment } from '../types';
import ArchitectureView2D from './ArchitectureView2D';
import projectAssetService, { type ProjectAsset } from '../../../../../services/projectAssetService';
import { equipmentService } from '../../../../../services/equipmentService';
import { projectService } from '../../../../../services/projectService';
import {
  ensureSolarCatalog,
  getMissingRequiredRoles,
  rolesNeededForPlan
} from '../solarSetupCatalog';
import {
  buildSolarWizardBulkPlan,
  capacityToKw,
  expandSolarLayoutToVirtualNodes,
  isSolarDcLayout,
  SOLAR_DC_LAYOUT_EQUIPMENT_TYPE,
  suggestEquipmentCounts,
  validateSolarPlanCounts,
  type CapacityInput,
  type SolarDcLayout,
  type SolarPlanCounts
} from '../solarPlantSizing';

type Step =
  | 'intro'
  | 'capacity'
  | 'mount'
  | 'countsMode'
  | 'suggestedCounts'
  | 'manualInverters'
  | 'manualStrings'
  | 'manualScb'
  | 'manualTransformers'
  | 'manualAc'
  | 'review';

export type SolarPlantSetupWizardProps = {
  open: boolean;
  onClose: () => void;
  projectId: string;
  project?: {
    _id?: string;
    id?: string;
    projectCategory?: string;
    legacyProjectCategory?: string;
    capacity?: { installedCapacity?: number; unit?: 'MW' | 'KW' | 'Units' };
  };
  availableAssets: AvailableAssetRef[];
  projectAssets: ProjectAsset[];
  onCompleted: () => void | Promise<void>;
};

function defaultCapacity(): CapacityInput {
  return { value: 1, unit: 'MW' };
}

function bulkItemsToVisual(
  items: Array<{
    _id: string;
    equipmentName: string;
    parentId: string | null;
    assetType: string;
    solarDcLayout?: SolarDcLayout;
  }>
): VisualEquipment[] {
  const base: VisualEquipment[] = items.map(it => ({
    id: it._id,
    parentEquipmentId: it.parentId,
    childEquipmentIds: [],
    name: it.equipmentName,
    assetType: it.assetType,
    projectAssetName: it.assetType,
    solarDcLayout: it.solarDcLayout && isSolarDcLayout(it.solarDcLayout) ? it.solarDcLayout : undefined
  }));
  const byId = new Map(base.map(e => [e.id, { ...e, childEquipmentIds: [...e.childEquipmentIds] }]));
  const extras: VisualEquipment[] = [];
  for (const it of items) {
    if (!it.solarDcLayout || !isSolarDcLayout(it.solarDcLayout)) continue;
    const layout = it.solarDcLayout;
    const isDcLayoutRow =
      String(it.assetType || '').trim().toLowerCase() === SOLAR_DC_LAYOUT_EQUIPMENT_TYPE.toLowerCase();
    const isInv = it.assetType === 'Inverter';
    const isScb =
      /combiner/i.test(it.assetType) && !/inverter/i.test(it.assetType);
    if (!isDcLayoutRow && !isInv && !isScb) continue;
    if (isInv && layout.combinerBoxCount > 0) continue;
    const v = expandSolarLayoutToVirtualNodes(it._id, it.equipmentName, layout);
    extras.push(...v);
    const parent = byId.get(it._id);
    if (parent) {
      const childIds = v.filter(x => x.parentEquipmentId === it._id).map(x => x.id);
      parent.childEquipmentIds = [...parent.childEquipmentIds, ...childIds];
    }
  }
  return [...Array.from(byId.values()), ...extras];
}

export default function SolarPlantSetupWizard({
  open,
  onClose,
  projectId,
  project,
  availableAssets,
  projectAssets,
  onCompleted
}: SolarPlantSetupWizardProps) {
  const [step, setStep] = useState<Step>('intro');
  const [capacity, setCapacity] = useState<CapacityInput>(defaultCapacity);
  const [mountType, setMountType] = useState<'ground' | 'rooftop' | null>(null);
  const [countsMode, setCountsMode] = useState<'suggested' | 'manual' | null>(null);
  const [counts, setCounts] = useState<SolarPlanCounts>(() => suggestEquipmentCounts(defaultCapacity()));
  const [manualInv, setManualInv] = useState('2');
  const [manualStr, setManualStr] = useState('8');
  const [manualMod, setManualMod] = useState('20');
  const [manualScb, setManualScb] = useState('0');
  const [manualTrf, setManualTrf] = useState('1');
  const [manualAc, setManualAc] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resolveBusy, setResolveBusy] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [adjustPrompt, setAdjustPrompt] = useState<string | null>(null);

  const missingMasters = useMemo(() => getMissingRequiredRoles(availableAssets), [availableAssets]);

  const reset = useCallback(() => {
    setStep('intro');
    setCapacity(defaultCapacity());
    setMountType(null);
    setCountsMode(null);
    setCounts(suggestEquipmentCounts(defaultCapacity()));
    setManualInv('2');
    setManualStr('8');
    setManualMod('20');
    setManualScb('0');
    setManualTrf('1');
    setManualAc(true);
    setCatalogError(null);
    setAdjustPrompt(null);
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const applySuggestedCounts = useCallback(() => {
    setCounts(suggestEquipmentCounts(capacity));
  }, [capacity]);

  useEffect(() => {
    if (step === 'suggestedCounts') {
      applySuggestedCounts();
    }
  }, [step, applySuggestedCounts]);

  const goNextFromCountsMode = (mode: 'suggested' | 'manual') => {
    setCountsMode(mode);
    if (mode === 'suggested') {
      setStep('suggestedCounts');
      return;
    }
    setManualInv(String(Math.max(1, counts.inverters)));
    setManualStr(String(Math.max(1, counts.stringsPerInverter)));
    setManualMod(String(Math.max(1, counts.modulesPerString)));
    setManualScb(String(counts.combinerCount));
    setManualTrf(String(Math.max(0, counts.transformerCount)));
    setManualAc(counts.includeAcSide);
    setStep('manualInverters');
  };

  const commitManualCounts = () => {
    const inv = Math.max(1, parseInt(manualInv, 10) || 1);
    const str = Math.max(1, parseInt(manualStr, 10) || 1);
    const mod = Math.max(1, parseInt(manualMod, 10) || 1);
    const scb = Math.max(0, parseInt(manualScb, 10) || 0);
    const trf = Math.max(0, parseInt(manualTrf, 10) || 0);
    setCounts({
      inverters: inv,
      stringsPerInverter: str,
      modulesPerString: mod,
      combinerCount: scb,
      transformerCount: trf < 1 && manualAc ? 1 : trf,
      includeAcSide: manualAc,
      includeDcCable: false
    });
    setStep('review');
  };

  const [previewVisual, setPreviewVisual] = useState<VisualEquipment[]>([]);

  useEffect(() => {
    if (open && project?.capacity?.installedCapacity != null && project.capacity.unit) {
      const u = project.capacity.unit === 'Units' ? 'KW' : project.capacity.unit;
      setCapacity({
        value: project.capacity.installedCapacity,
        unit: u === 'MW' ? 'MW' : 'KW'
      });
    }
  }, [open, project]);

  useEffect(() => {
    if (step !== 'review') {
      setPreviewVisual([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setResolveBusy(true);
      setCatalogError(null);
      try {
        const roles = rolesNeededForPlan({
          useCombiner: counts.combinerCount > 0,
          includeAcSide: counts.includeAcSide,
          includeDcCable: counts.includeDcCable,
          includeExtras: false
        });
        const { resolved, missingMasters: miss } = await ensureSolarCatalog(
          projectId,
          availableAssets,
          projectAssets,
          roles,
          (pid, mid) => projectAssetService.createOrGetProjectAsset(pid, mid)
        );
        if (miss.length > 0) {
          setCatalogError(
            `Missing master assets for: ${miss.join(', ')}. Add them to the project type, then add to this project.`
          );
          setPreviewVisual([]);
          return;
        }
        const { items } = buildSolarWizardBulkPlan(projectId, counts, resolved);
        if (!cancelled) setPreviewVisual(bulkItemsToVisual(items));
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not build preview.';
        if (!cancelled) {
          setCatalogError(msg);
          setPreviewVisual([]);
        }
      } finally {
        if (!cancelled) setResolveBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, projectId, availableAssets, projectAssets, counts]);

  const handleSave = async () => {
    if (missingMasters.length > 0) {
      toast.error('Add an Inverter master asset to the project type and project before saving.');
      return;
    }
    const v0 = validateSolarPlanCounts(counts);
    if (!v0.ok) {
      if (v0.fixed) {
        setAdjustPrompt(v0.messages.join(' '));
        return;
      }
      toast.error(v0.messages.join(' '));
      return;
    }
    const finalCounts = counts;

    setSaving(true);
    try {
      const roles = rolesNeededForPlan({
        useCombiner: finalCounts.combinerCount > 0,
        includeAcSide: finalCounts.includeAcSide,
        includeDcCable: finalCounts.includeDcCable,
        includeExtras: false
      });
      const { resolved, missingMasters: miss } = await ensureSolarCatalog(
        projectId,
        availableAssets,
        projectAssets,
        roles,
        (pid, mid) => projectAssetService.createOrGetProjectAsset(pid, mid)
      );
      if (miss.length > 0) {
        toast.error(`Missing catalog: ${miss.join(', ')}`);
        return;
      }
      const { items } = buildSolarWizardBulkPlan(projectId, finalCounts, resolved);
      const payload = items.map(it => ({
        ...it,
        status: 'active',
        statusBadge: 'active'
      }));
      const bulkRes = await equipmentService.createBulkEquipment({ projectId, items: payload });
      const createdCount = bulkRes.data?.totalCreated ?? 0;
      const failedCount = bulkRes.data?.totalFailed ?? 0;
      const batched = bulkRes.meta?.batched;
      if (failedCount > 0) {
        toast.warning(
          `${createdCount} items saved, ${failedCount} failed.${batched ? ` (${bulkRes.meta?.batchCount} batches)` : ''}`
        );
      } else if (batched) {
        toast.success(
          `Saved ${createdCount} equipment items in ${bulkRes.meta?.batchCount} batches (large plant).`
        );
      }

      const kw = capacityToKw(capacity);
      const unit: 'MW' | 'KW' = capacity.unit;
      const installed = unit === 'MW' ? capacity.value : kw;
      try {
        await projectService.updateProject(projectId, {
          capacity: {
            installedCapacity: installed,
            unit
          }
        });
      } catch {
        /* optional — project patch may fail if permissions; equipment is already saved */
      }

      void mountType;

      if (failedCount === 0 && !batched) {
        toast.success('Solar plant equipment has been saved.');
      }
      await onCompleted();
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const applyAutoFix = () => {
    const v = validateSolarPlanCounts(counts);
    if (v.fixed) setCounts(v.fixed);
    setAdjustPrompt(null);
    setStep('review');
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100">
              <Sun className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">AI Project Architecte</h2>
              <p className="text-[11px] text-slate-500">One step at a time</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {missingMasters.length > 0 && (
          <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-[11px] text-amber-900">
            Your project type must include master assets for:{' '}
            {missingMasters.map(m => (m === 'inverter' ? 'Inverter' : m)).join(', ')}.
          </div>
        )}

        <div className="min-h-[280px] flex-1 overflow-y-auto px-4 py-4">
          <AnimatePresence mode="wait">
            {step === 'intro' && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="space-y-4"
              >
                <p className="text-sm leading-relaxed text-slate-700">
                  Hi! I&apos;ll help you create a solar plant project step by step.
                  Let&apos;s start with some basic details.
                </p>
                <button
                  type="button"
                  onClick={() => setStep('capacity')}
                  className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 py-3 text-sm font-semibold text-white shadow-md transition hover:from-amber-400 hover:to-orange-400"
                >
                  Let&apos;s begin
                </button>
              </motion.div>
            )}

            {step === 'capacity' && (
              <motion.div
                key="capacity"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="space-y-4"
              >
                <p className="text-sm font-medium text-slate-800">What is your plant capacity?</p>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={0.01}
                    step={0.01}
                    value={capacity.value}
                    onChange={e => setCapacity(c => ({ ...c, value: parseFloat(e.target.value) || 0 }))}
                    className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  />
                  <select
                    value={capacity.unit}
                    onChange={e => setCapacity(c => ({ ...c, unit: e.target.value as 'MW' | 'KW' }))}
                    className="rounded-xl border border-slate-200 px-2 py-2 text-sm"
                  >
                    <option value="MW">MW</option>
                    <option value="KW">kW</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => setStep('mount')}
                  className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white"
                >
                  Next
                </button>
              </motion.div>
            )}

            {step === 'mount' && (
              <motion.div
                key="mount"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="space-y-4"
              >
                <p className="text-sm font-medium text-slate-800">Is it ground-mounted or rooftop?</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setMountType('ground')}
                    className={`rounded-xl border-2 py-3 text-sm font-semibold ${
                      mountType === 'ground'
                        ? 'border-amber-500 bg-amber-50 text-amber-900'
                        : 'border-slate-200 text-slate-700'
                    }`}
                  >
                    Ground
                  </button>
                  <button
                    type="button"
                    onClick={() => setMountType('rooftop')}
                    className={`rounded-xl border-2 py-3 text-sm font-semibold ${
                      mountType === 'rooftop'
                        ? 'border-amber-500 bg-amber-50 text-amber-900'
                        : 'border-slate-200 text-slate-700'
                    }`}
                  >
                    Rooftop
                  </button>
                </div>
                <button
                  type="button"
                  disabled={!mountType}
                  onClick={() => setStep('countsMode')}
                  className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white disabled:opacity-40"
                >
                  Next
                </button>
              </motion.div>
            )}

            {step === 'countsMode' && (
              <motion.div
                key="countsMode"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="space-y-4"
              >
                <p className="text-sm font-medium text-slate-800">
                  I can estimate modules, strings, inverters, and combiner boxes from typical designs.
                  How would you like to continue?
                </p>
                <button
                  type="button"
                  onClick={() => goNextFromCountsMode('suggested')}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 py-3 text-sm font-semibold text-white"
                >
                  <Sparkles size={16} />
                  Use suggested counts
                </button>
                <button
                  type="button"
                  onClick={() => goNextFromCountsMode('manual')}
                  className="w-full rounded-xl border-2 border-slate-200 py-3 text-sm font-semibold text-slate-800"
                >
                  I&apos;ll enter counts myself
                </button>
              </motion.div>
            )}

            {step === 'suggestedCounts' && (
              <motion.div
                key="suggestedCounts"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                className="space-y-3"
              >
                <p className="text-sm font-medium text-slate-800">Here is a starting layout you can tweak.</p>
                <div className="space-y-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-700">
                  <label className="flex justify-between gap-2">
                    <span>Inverters</span>
                    <input
                      type="number"
                      min={1}
                      className="w-20 rounded border px-2 py-1"
                      value={counts.inverters}
                      onChange={e =>
                        setCounts(c => ({ ...c, inverters: Math.max(1, parseInt(e.target.value, 10) || 1) }))
                      }
                    />
                  </label>
                  <label className="flex justify-between gap-2">
                    <span>Strings per inverter</span>
                    <input
                      type="number"
                      min={1}
                      className="w-20 rounded border px-2 py-1"
                      value={counts.stringsPerInverter}
                      onChange={e =>
                        setCounts(c => ({
                          ...c,
                          stringsPerInverter: Math.max(1, parseInt(e.target.value, 10) || 1)
                        }))
                      }
                    />
                  </label>
                  <label className="flex justify-between gap-2">
                    <span>Modules per string</span>
                    <input
                      type="number"
                      min={1}
                      className="w-20 rounded border px-2 py-1"
                      value={counts.modulesPerString}
                      onChange={e =>
                        setCounts(c => ({
                          ...c,
                          modulesPerString: Math.max(1, parseInt(e.target.value, 10) || 1)
                        }))
                      }
                    />
                  </label>
                  <label className="flex justify-between gap-2">
                    <span>String combiner boxes (0 = none)</span>
                    <input
                      type="number"
                      min={0}
                      className="w-20 rounded border px-2 py-1"
                      value={counts.combinerCount}
                      onChange={e =>
                        setCounts(c => ({
                          ...c,
                          combinerCount: Math.max(0, parseInt(e.target.value, 10) || 0)
                        }))
                      }
                    />
                  </label>
                  <label className="flex justify-between gap-2">
                    <span>Transformers</span>
                    <input
                      type="number"
                      min={0}
                      className="w-20 rounded border px-2 py-1"
                      value={counts.transformerCount}
                      onChange={e =>
                        setCounts(c => ({
                          ...c,
                          transformerCount: Math.max(0, parseInt(e.target.value, 10) || 0)
                        }))
                      }
                    />
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={counts.includeAcSide}
                      onChange={e => setCounts(c => ({ ...c, includeAcSide: e.target.checked }))}
                    />
                    <span>Include AC path (cable, LT panel, transformer) when assets exist</span>
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => setStep('review')}
                  className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white"
                >
                  Review plan
                </button>
              </motion.div>
            )}

            {step === 'manualInverters' && (
              <motion.div
                key="mi"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <p className="text-sm font-medium text-slate-800">How many inverters?</p>
                <input
                  type="number"
                  min={1}
                  value={manualInv}
                  onChange={e => setManualInv(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setStep('manualStrings')}
                  className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white"
                >
                  Next
                </button>
              </motion.div>
            )}

            {step === 'manualStrings' && (
              <motion.div
                key="ms"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <p className="text-sm font-medium text-slate-800">How many strings per inverter?</p>
                <input
                  type="number"
                  min={1}
                  value={manualStr}
                  onChange={e => setManualStr(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
                <p className="text-xs text-slate-500">How many modules on each string?</p>
                <input
                  type="number"
                  min={1}
                  value={manualMod}
                  onChange={e => setManualMod(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setStep('manualScb')}
                  className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white"
                >
                  Next
                </button>
              </motion.div>
            )}

            {step === 'manualScb' && (
              <motion.div
                key="mscb"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <p className="text-sm font-medium text-slate-800">
                  How many string combiner boxes? (Enter 0 if you do not use them.)
                </p>
                <input
                  type="number"
                  min={0}
                  value={manualScb}
                  onChange={e => setManualScb(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setStep('manualTransformers')}
                  className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white"
                >
                  Next
                </button>
              </motion.div>
            )}

            {step === 'manualTransformers' && (
              <motion.div
                key="mtrf"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <p className="text-sm font-medium text-slate-800">How many transformers?</p>
                <input
                  type="number"
                  min={0}
                  value={manualTrf}
                  onChange={e => setManualTrf(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setStep('manualAc')}
                  className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white"
                >
                  Next
                </button>
              </motion.div>
            )}

            {step === 'manualAc' && (
              <motion.div
                key="mac"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <label className="flex items-start gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={manualAc}
                    onChange={e => setManualAc(e.target.checked)}
                  />
                  <span>Add AC equipment (AC cable, LT panel, transformer) when those assets exist on the project.</span>
                </label>
                <button
                  type="button"
                  onClick={commitManualCounts}
                  className="w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white"
                >
                  Review plan
                </button>
              </motion.div>
            )}

            {step === 'review' && (
              <motion.div
                key="review"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <p className="text-sm font-semibold text-slate-900">Here is your plant setup</p>
                <ul className="list-inside list-disc text-xs text-slate-600">
                  <li>Inverters: {counts.inverters}</li>
                  <li>Strings: {counts.inverters * counts.stringsPerInverter}</li>
                  <li>Modules per string: {counts.modulesPerString}</li>
                  <li>
                    Total modules: {counts.inverters * counts.stringsPerInverter * counts.modulesPerString}
                  </li>
                  <li>
                    Combiner boxes: {counts.combinerCount === 0 ? 'None' : counts.combinerCount} (per inverter)
                  </li>
                  <li>Transformers (target): {counts.transformerCount}</li>
                  <li>AC path: {counts.includeAcSide ? 'Yes (if assets exist)' : 'No'}</li>
                </ul>

                {adjustPrompt && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950">
                    <p className="mb-2">
                      Based on typical solar design, this configuration may not be optimal. Adjust automatically?
                    </p>
                    <p className="mb-2 text-amber-800">{adjustPrompt}</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={applyAutoFix}
                        className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white"
                      >
                        Fix for me
                      </button>
                      <button
                        type="button"
                        onClick={() => setAdjustPrompt(null)}
                        className="rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-900"
                      >
                        Keep mine
                      </button>
                    </div>
                  </div>
                )}

                {catalogError && (
                  <p className="rounded-lg bg-red-50 p-2 text-xs text-red-800">{catalogError}</p>
                )}

                {resolveBusy && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Building preview…
                  </div>
                )}

                {!resolveBusy && previewVisual.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Architecture preview
                    </p>
                    <div className="max-h-56 overflow-auto rounded-lg bg-white">
                      <ArchitectureView2D equipment={previewVisual} zoomLevel={1} availableAssets={availableAssets} />
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    disabled={saving || !!catalogError || missingMasters.length > 0 || resolveBusy}
                    onClick={() => void handleSave()}
                    className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 py-3 text-sm font-semibold text-white disabled:opacity-40"
                  >
                    {saving ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                      </span>
                    ) : (
                      'Save this project'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (countsMode === 'manual') setStep('manualInverters');
                      else setStep('suggestedCounts');
                    }}
                    className="w-full rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-700"
                  >
                    Edit configuration
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
